import React from 'react';
import { useAccordionItem } from '../AccordionItemContext';
import styles from './AccordionHeader.module.scss';
import { Button, Icon, IconList } from 'components/core';

// level indicates the heading level (h1, h2, h3) for accessibility
// use different levels for nested accordions
type Props = {
  children: React.ReactNode;
  className?: string;
  chevronPosition?: 'left' | 'right';
  disabled?: boolean;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
};

const hasControlChildren = (elem: React.ReactNode): boolean => {
  let isControl = false;
  if (React.isValidElement(elem)) {
    ['href', 'to', 'onClick'].forEach((prop) => {
      if (elem.props[prop]) {
        isControl = true;
      }
    });

    if (!isControl && elem.props.children) {
      React.Children.forEach(
        elem.props.children,
        (child) => (isControl = hasControlChildren(child))
      );
    }
  }
  return isControl;
};

export const AccordionHeader = ({
  className,
  chevronPosition = 'left',
  disabled = false,
  children,
  level = 2,
}: Props) => {
  const {
    isOpen,
    setIsOpen,
    buttonId,
    contentId,
    handleKeyDown,
    buttonRef,
    disabled: contextDisabled,
  } = useAccordionItem();

  const isDisabled = disabled || contextDisabled;
  const classes = [styles.AccordionHeader];
  if (className) classes.push(className);
  if (isOpen) classes.push(styles.AccordionHeader_open);

  const displayChildren: React.ReactNode[] = [];
  const controlChildren: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (hasControlChildren(child)) {
      controlChildren.push(child);
    } else {
      displayChildren.push(child);
    }
  });

  return (
    <div className={classes.join(' ')}>
      <div role="heading" aria-level={level}>
        <Button
          ref={buttonRef}
          id={buttonId}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          onKeyDown={handleKeyDown}
          aria-expanded={isOpen}
          aria-controls={contentId}
          disabled={isDisabled}
          isNaked={true}
          className={styles.AccordionHeader_button}
        >
          {chevronPosition === 'left' && (
            <Icon
              icon={
                isOpen
                  ? IconList.light.faChevronUp
                  : IconList.light.faChevronDown
              }
              aria-hidden="true"
            />
          )}
          {displayChildren}
        </Button>
      </div>
      {controlChildren.length || chevronPosition === 'right' ? (
        <div className={styles.AccordionControlsContainer}>
          {controlChildren}
          {chevronPosition === 'right' && (
            <Icon
              className={styles.RighthandChevron}
              icon={
                isOpen
                  ? IconList.light.faChevronUp
                  : IconList.light.faChevronDown
              }
              aria-hidden="true"
            />
          )}
        </div>
      ) : undefined}
    </div>
  );
};
